/* @jsx, jsx */
import { css } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import mapProps from 'js/lib/mapProps';
import path from 'js/lib/path';
import waitFor from 'js/lib/waitFor';

import { Typography, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { PaginationControls } from '@coursera/coursera-ui';

import type { CMLDefinition } from 'bundles/compound-assessments/types/FormParts';
import SearchActions from 'bundles/discussions/actions/DiscussionsSearchActions';
import ListBody from 'bundles/discussions/components/ListBody';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import SearchResultEntry from 'bundles/discussions/components/personalization-Search/SearchResultEntry';
import { loadingStates, naptimeForumTypes } from 'bundles/discussions/constants';
import type { LoadingState } from 'bundles/discussions/constants';
import type { AuthorProfile } from 'bundles/discussions/lib/types';
import { getAllThreadsDiscussionsUrl } from 'bundles/discussions/utils/discussionsUrl';
import routerConnectToCurrentForum from 'bundles/discussions/utils/routerConnectToCurrentForum';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type GroupForumsV1 from 'bundles/naptimejs/resources/groupForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseForumsV1 from 'bundles/naptimejs/resources/onDemandCourseForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandMentorForumsV1 from 'bundles/naptimejs/resources/onDemandMentorForums.v1';

import _t from 'i18n!nls/discussions';

import 'css!bundles/page/components/__styles__/PaginationControls';

export type SearchResultType = {
  createdAt: number;
  creatorId: number;
  content: CMLDefinition;
  snippet: CMLDefinition;
  threadName: string;
  creator: AuthorProfile;
  id: string;
  courseForumQuestionId: string;
  title: string;
  questionId: string;
};

export type PropsFromStores = {
  numResults?: number;
  loadingState: LoadingState;
  pageCount?: number;
  currentResults: SearchResultType[];
  filterQueryString?: string;
  rootForumId?: string;
};

export type PropsFromCaller = {
  currentForum: OnDemandCourseForumsV1 | OnDemandMentorForumsV1 | GroupForumsV1;
  query: string;
  pathname: string;
  page: number;
  rootPath?: string;
  currentForumUrl?: string;
  hasModerationRole?: boolean;
  contextId?: string;
  rootForumId?: string;
};

type State = {
  query: string;
};

type PropsToComponent = PropsFromCaller & PropsFromStores;
export const styles = (theme: Theme) => css`
  .back-area {
    margin: var(--cds-spacing-100) 0 var(--cds-spacing-150);
    display: inline-block;

    .back-label {
      display: inline-block;
      margin: 0 var(--cds-spacing-100);
    }

    .back-arrow {
      display: inline-block;
      padding-left: 0 var(--cds-spacing-50);
      margin-left: 0 var(--cds-spacing-100);
      transition: all 0.1s ease-in-out;

      .back-arrow-image {
        margin-bottom: 0 var(--cds-spacing-50);
        height: 12px;
        width: 12px;
      }
    }

    &hover {
      .back-arrow {
        padding-left: 0;
        padding-right: 0 var(--cds-spacing-50);
        transition: all 0.1s ease-in-out;
      }
    }
  }

  .results-metadata {
    padding: 0 0 var(--cds-spacing-100) 0;
    line-height: 28px;
    letter-spacing: -0.1px;
    color: ${theme.palette.black};
    border-bottom: 1px solid var(--cds-color-grey-50);

    .search-course-link {
      text-decoration: underline;
    }
  }

  .rc-SearchResultEntry {
    cursor: pointer;
    border-bottom: 1px solid var(--cds-color-grey-50);
    list-style-type: none;

    .c-result-title {
      margin-bottom: var(--cds-spacing-50);
      font-weight: bold;
    }

    &hover {
      .c-result-title {
        text-decoration: underline;
      }
    }

    &last-child {
      border-bottom: none;
    }
  }

  .rc-ListBody {
    padding: 0;
  }
`;

const RenderPropsTheme = ({ children }: { children: ({ theme }: { theme: Theme }) => JSX.Element }) => {
  const theme = useTheme();
  return children({ theme });
};

class SearchResults extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof SearchResults.contextTypes>;

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);
    // Setting initial state for initial display in input
    this.state = {
      query: props.query || '',
    };
    if (props.query) {
      const { executeAction } = this.context;
      executeAction(SearchActions.search, {
        query: props.query,
        filterQueryString: props.filterQueryString,
        pageNum: props.page,
        forumId: props.currentForum.forumId,
        contextId: props.contextId,
        forumType: props.currentForum.forumType.typeName,
        reset: true,
        includeDeleted: props.hasModerationRole,
      });
    }
  }

  componentWillReceiveProps(nextProps: PropsToComponent) {
    // TODO(lewis): Figure out if there is a way to do without this since it's
    // fragile to have to define componentWillReceiveProps to determine whether
    // or not we fire off an expensive search action.
    const { query, pathname, currentForum, filterQueryString, page } = this.props;
    const { executeAction } = this.context;
    if (
      nextProps.query !== query ||
      nextProps.pathname !== pathname ||
      nextProps.currentForum.id !== currentForum.id ||
      nextProps.filterQueryString !== filterQueryString
    ) {
      this.setState({ query: nextProps.query });
      executeAction(SearchActions.search, {
        query: nextProps.query,
        filterQueryString: nextProps.filterQueryString,
        pageNum: page,
        forumId: nextProps.currentForum.forumId,
        contextId: nextProps.contextId,
        forumType: nextProps.currentForum.forumType.typeName,
        reset: filterQueryString !== nextProps.filterQueryString,
        includeDeleted: nextProps.hasModerationRole,
      });
    }
  }

  handleQueryChange = (newQuery: $TSFixMe) => {
    this.setState({ query: newQuery });
  };

  handleSearchCourse = (e: $TSFixMe) => {
    const { query } = this.state;
    const { rootPath } = this.props;
    const { router } = this.context;
    e.preventDefault();
    const link = path.join(rootPath ?? [], 'discussions/all');
    router.push({
      pathname: link,
      params: {},
      query: { q: query },
    });
  };

  onPageChange = (page: $TSFixMe) => {
    const { query, currentForum, contextId, hasModerationRole } = this.props;
    const { router, executeAction } = this.context;
    router.push({
      pathname: router.location.pathname,
      params: {},
      query: Object.assign(router.location.query, { page }),
    });
    executeAction(SearchActions.goToPage, {
      query,
      pageNum: page,
      forumId: currentForum.forumId,
      filterQueryString: this.props.filterQueryString,
      contextId,
      forumType: currentForum.forumType.typeName,
      includeDeleted: hasModerationRole,
    });
  };

  submitSearch = () => {
    const { query } = this.state;
    const { router } = this.context;
    if (query) {
      const queryString = query.replace('*', '');
      router.push({
        pathname: window.location.pathname,
        params: {},
        query: { q: queryString },
      });
    }
  };

  navigateBack = (e: $TSFixMe) => {
    const { router } = this.context;
    const { currentForumUrl } = this.props;
    e.preventDefault();
    router.push(currentForumUrl);
  };

  render() {
    const {
      currentForum,
      currentForumUrl,
      loadingState,
      numResults,
      currentResults,
      page,
      pageCount,
      query,
      rootForumId,
      rootPath,
    } = this.props;
    const { router } = this.context;
    const inAllDiscussions = router.isActive({
      pathname: getAllThreadsDiscussionsUrl(),
    });
    const displayName = currentForum.title;
    const forumEntryId = rootForumId?.substr(rootForumId.indexOf('~') + 1);
    const rootForumLink = path.join(rootPath ?? [], `discussions/forums/${forumEntryId}`);
    const discussionsPathTest = /discussions\/(forums|weeks|groups)\//i;
    const isValidForumPath = discussionsPathTest.test(router.location.pathname);
    const forumLink = isValidForumPath ? currentForumUrl : rootForumLink;

    let searchResultsText;
    if (loadingState === loadingStates.DONE) {
      if (numResults && numResults > 1) {
        searchResultsText = _t('#{numResults} Discussions', { numResults });
      } else {
        searchResultsText = _t('#{numResults} Discussion', { numResults });
      }
    } else {
      searchResultsText = _t('Finding search results in #{displayName}', { displayName });
    }
    let entries: $TSFixMe;

    const resultsMetadata = (
      <div className="results-metadata">
        <Typography variant="h2semibold" aria-live="polite">
          {/* Took out the FormattedMessage component and opted to manually handle plurals because the search results
          weren't being properly announced for users using Firefox + NVDA. More info here  https://coursera.atlassian.net/browse/ACCESS-568 */}
          {loadingState === loadingStates.DONE ? (
            <span>{searchResultsText}</span>
          ) : (
            <FormattedMessage message={_t('Finding search results in {displayName}')} displayName={displayName} />
          )}
        </Typography>
      </div>
    );

    if (loadingState === loadingStates.DONE && currentResults.length > 0) {
      entries = currentResults.map((entry, i) => {
        return (
          <SearchResultEntry
            page={page}
            query={query}
            forumLink={forumLink || ''}
            entry={entry}
            index={i}
            key={entry.id}
          />
        );
      });
    }

    return (
      <RenderPropsTheme>
        {({ theme }) => (
          <div className="rc-personalization__DiscussionsSearchResults" css={styles(theme)}>
            {!inAllDiscussions && resultsMetadata}
            <ListBody loadingState={loadingState}>{entries}</ListBody>
            <PaginationControls
              style={{ textAlign: 'center' }}
              onClickHandler={this.onPageChange}
              pageCount={pageCount || 1}
              currentPage={page}
              maxPages={5}
            />
          </div>
        )}
      </RenderPropsTheme>
    );
  }
}
export default compose<PropsToComponent, PropsFromCaller>(
  connectToRouter((router) => ({
    query: decodeURIComponent(router.location.query.q),
    pathname: router.location.pathname,
  })),
  discussionsForumsHOC({ fields: ['title', 'link', 'forumType'] }),
  connectToRouter(routerConnectToCurrentForum),
  connectToStores<PropsToComponent, PropsFromCaller>(
    ['DiscussionsSearchStore', 'SessionFilterStore'],
    ({ DiscussionsSearchStore, SessionFilterStore }, props) => {
      const id = props.currentForum.forumId;
      return {
        filterQueryString: SessionFilterStore.activeFilterQueryString,
        currentResults: DiscussionsSearchStore.searchResults({
          forumId: id,
          query: props.query,
          pageNum: DiscussionsSearchStore.currentPage,
        }),
        loadingState: DiscussionsSearchStore.loadingState,
        pageCount: DiscussionsSearchStore.getPageCount({
          forumId: id,
          query: props.query,
        }),
        numResults: DiscussionsSearchStore.getNumResults({
          forumId: id,
          query: props.query,
        }),
      };
    }
  ),
  mapProps<any, any>((props) => {
    const rootForum =
      props.courseForums &&
      props.courseForums.find((forum: $TSFixMe) => forum.forumType.typeName === naptimeForumTypes.rootForumType);
    return {
      forums: props.courseForums
        ? props.courseForums
            .filter((forum: $TSFixMe) => forum.forumType.typeName !== naptimeForumTypes.rootForumType)
            .concat(props.mentorForums)
            .concat(props.groupForums)
        : [],
      rootForumId: rootForum && rootForum.id,
    };
  }),
  // make sure that we have a forum to search in
  waitFor((props) => props.currentForum)
)(SearchResults);

export const _private = {
  SearchResults,
};
